import React, { useState, useEffect, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { Api } from '../generated/Api';
import { config as appConfig } from '../config/config';
import {
  StatisticsResponseDto,
  ClientStatisticsResponseDto,
} from '../generated/data-contracts';
import { authService } from '../services/authService';

export const useStatisticsApi = () => {
  const notify = useNotify();
  const [api, setApi] = useState<Api<unknown> | null>(null);

  useEffect(() => {
    const apiInstance = new Api({
      baseURL: appConfig.apiUrl,
    });

    // ✅ UNIFICADO - Usar authService
    apiInstance.instance.interceptors.request.use(configReq => {
      const token = authService.getAccessToken();
      if (token) {
        configReq.headers.Authorization = `Bearer ${token}`;
      }
      return configReq;
    });

    // INTERCEPTOR DE RESPUESTA - MANEJA 401/403
    apiInstance.instance.interceptors.response.use(
      response => response,
      error => {
        const status = error.response?.status;

        if (status === 401 || status === 403) {
          console.log('🔄 Statistics API: Token inválido detectado');
          // ✅ UNIFICADO - Usar authService.logout()
          setTimeout(() => {
            authService.logout();
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }, 100);
        }

        return Promise.reject(error);
      }
    );

    setApi(apiInstance);
  }, []);

  const getGeneralStatistics =
    useCallback(async (): Promise<StatisticsResponseDto | null> => {
      if (!api) return null;
      try {
        const response = await api.statisticsGeneralList();
        return response.data;
      } catch (error: any) {
        console.error('Error fetching general statistics:', error);

        // NO NOTIFICAR SI ES ERROR DE AUTENTICACIÓN
        if (error.response?.status !== 401 && error.response?.status !== 403) {
          notify('Error al cargar las estadísticas generales', {
            type: 'error',
          });
        }

        return null;
      }
    }, [api, notify]);

  const getClientStatistics = useCallback(async (): Promise<
    ClientStatisticsResponseDto[] | null
  > => {
    if (!api) return null;
    try {
      const response = await api.statisticsByClientList();
      return response.data;
    } catch (error: any) {
      console.error('Error fetching client statistics:', error);

      // NO NOTIFICAR SI ES ERROR DE AUTENTICACIÓN
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        notify('Error al cargar las estadísticas por cliente', {
          type: 'error',
        });
      }

      return [];
    }
  }, [api, notify]);

  return { getGeneralStatistics, getClientStatistics, api };
};
